import type { OnboardingDetail } from '../api/onboarding-types'
import { formatDate } from './dates'

/** The four steps of a new-product request, for the NextStepBar. */
export type StepState = 'done' | 'current' | 'todo' | 'failed' | 'stopped'

export interface Step {
  key: string
  label: string
  state: StepState
  detail: string | null
}

export type StepDetail = Pick<OnboardingDetail, 'status' | 'submitted_by' | 'submitted_at' | 'regulatory_signed_by' |
  'regulatory_signed_at' | 'commercial_signed_by' | 'commercial_signed_at' | 'pushed_at'>

const who = (email: string | null) => (email ? email.split('@')[0] : null)
const signed = (by: string | null, at: string | null) => (by ? `${who(by)} · ${formatDate(at)}` : null)

export function onboardingSteps(d: StepDetail): Step[] {
  const s = d.status
  // Index of the step the request is at: 0 submit, 1 regulatory, 2 operations, 3 Odoo.
  const at = { awaiting_vendor: 0, draft: 0, regulatory_review: 1, commercial_review: 2, approved: 3,
    push_failed: 3, pushed_to_odoo: 4, cancelled: -1 }[s]
  const state = (i: number, done: boolean): StepState => {
    if (s === 'cancelled') return done ? 'done' : 'stopped'
    if (i < at) return 'done'
    if (i === at) return s === 'push_failed' ? 'failed' : 'current'
    return 'todo'
  }
  const reg = state(1, !!d.regulatory_signed_by)
  const ops = state(2, !!d.commercial_signed_by)
  return [
    { key: 'submit', label: d.submitted_by ? 'Submitted' : 'Submit for review', state: state(0, !!d.submitted_by),
      detail: d.submitted_by ? signed(d.submitted_by, d.submitted_at)
        : s === 'awaiting_vendor' ? 'Waiting for the vendor' : s === 'draft' ? 'Being filled in' : null },
    { key: 'regulatory', label: 'Regulatory sign-off', state: reg,
      detail: signed(d.regulatory_signed_by, d.regulatory_signed_at) ?? (reg === 'current' ? 'Waiting for the regulatory team' : null) },
    { key: 'operations', label: 'Operations sign-off', state: ops,
      detail: signed(d.commercial_signed_by, d.commercial_signed_at) ?? (ops === 'current' ? 'Waiting for operations' : null) },
    { key: 'odoo', label: 'Created in Odoo', state: state(3, false),
      detail: s === 'pushed_to_odoo' ? formatDate(d.pushed_at) : s === 'push_failed' ? 'Failed; see the message below'
        : s === 'approved' ? 'Ready to create' : s === 'cancelled' ? 'Cancelled' : null },
  ]
}

